import type { NextApiRequest, NextApiResponse } from 'next';
import { withAuth } from '../../../lib/apiAuth';
import { getSupabaseAdminClient } from '../../../lib/supabaseAdmin';

/**
 * Bank Accounts CRUD API
 * GET    /api/bank/accounts?empresa_id=X       - List bank accounts for empresa
 * POST   /api/bank/accounts                     - Create bank account
 * PUT    /api/bank/accounts                     - Update bank account
 * DELETE /api/bank/accounts?id=X                - Deactivate bank account (soft delete)
 */
async function handler(
  req: NextApiRequest,
  res: NextApiResponse,
  user: { id: string; email: string; roles: string[]; primaryRole: string }
) {
  const supabaseAdmin = getSupabaseAdminClient();

  try {
    // ─── GET: List bank accounts ───
    if (req.method === 'GET') {
      const { empresa_id } = req.query;

      if (!empresa_id) {
        return res.status(400).json({ success: false, error: 'empresa_id es obligatorio.' });
      }

      const { data, error } = await supabaseAdmin
        .from('bank_accounts')
        .select('*')
        .eq('empresa_id', empresa_id as string)
        .eq('activa', true)
        .order('nombre', { ascending: true });

      if (error) {
        // If table doesn't exist, return empty array instead of error
        if (error.message?.includes('does not exist') || error.code === '42P01') {
          console.warn('[API bank/accounts] Table bank_accounts does not exist, returning empty array');
          return res.status(200).json({ success: true, data: [] });
        }
        console.error('[API bank/accounts] Error listing accounts:', error);
        return res.status(500).json({ success: false, error: error.message });
      }

      return res.status(200).json({ success: true, data: data || [] });
    }

    // ─── POST: Create bank account ───
    if (req.method === 'POST') {
      const {
        empresa_id,
        nombre,
        iban,
        bic_swift,
        entidad_bancaria,
        numero_cuenta,
        subcuenta_contable,
        saldo_inicial,
        moneda,
      } = req.body;

      if (!empresa_id || !nombre) {
        return res.status(400).json({
          success: false,
          error: 'empresa_id y nombre son obligatorios.',
        });
      }

      // Check for duplicate IBAN within empresa
      if (iban) {
        const { data: existing } = await supabaseAdmin
          .from('bank_accounts')
          .select('id')
          .eq('empresa_id', empresa_id)
          .eq('iban', iban)
          .eq('activa', true)
          .maybeSingle();

        if (existing) {
          return res.status(409).json({
            success: false,
            error: 'Ya existe una cuenta bancaria activa con este IBAN para esta empresa.',
          });
        }
      }

      const { data, error } = await supabaseAdmin
        .from('bank_accounts')
        .insert({
          empresa_id,
          nombre,
          iban: iban || null,
          bic_swift: bic_swift || null,
          entidad_bancaria: entidad_bancaria || null,
          numero_cuenta: numero_cuenta || null,
          subcuenta_contable: subcuenta_contable || null,
          saldo_inicial: saldo_inicial ?? 0,
          saldo_actual: saldo_inicial ?? 0,
          moneda: moneda || 'EUR',
          activa: true,
        })
        .select()
        .single();

      if (error) {
        console.error('[API bank/accounts] Error creating account:', error);
        return res.status(500).json({ success: false, error: error.message });
      }

      return res.status(201).json({ success: true, data });
    }

    // ─── PUT: Update bank account ───
    if (req.method === 'PUT') {
      const { id, ...updates } = req.body;

      if (!id) {
        return res.status(400).json({ success: false, error: 'id es obligatorio.' });
      }

      if (!updates || Object.keys(updates).length === 0) {
        return res.status(400).json({ success: false, error: 'No se proporcionaron campos para actualizar.' });
      }

      // Remove fields that should not be updated directly
      delete updates.created_at;
      delete updates.saldo_actual;

      const updateData: Record<string, unknown> = { ...updates, updated_at: new Date().toISOString() };

      const { data, error } = await supabaseAdmin
        .from('bank_accounts')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('[API bank/accounts] Error updating account:', error);
        return res.status(500).json({ success: false, error: error.message });
      }

      if (!data) {
        return res.status(404).json({ success: false, error: 'Cuenta bancaria no encontrada.' });
      }

      return res.status(200).json({ success: true, data });
    }

    // ─── DELETE: Deactivate bank account (soft delete) ───
    if (req.method === 'DELETE') {
      const { id } = req.query;

      if (!id) {
        return res.status(400).json({ success: false, error: 'id es obligatorio.' });
      }

      const { data, error } = await supabaseAdmin
        .from('bank_accounts')
        .update({ activa: false, updated_at: new Date().toISOString() })
        .eq('id', id as string)
        .select()
        .single();

      if (error) {
        console.error('[API bank/accounts] Error deactivating account:', error);
        return res.status(500).json({ success: false, error: error.message });
      }

      if (!data) {
        return res.status(404).json({ success: false, error: 'Cuenta bancaria no encontrada.' });
      }

      return res.status(200).json({ success: true, data });
    }

    return res.status(405).json({ success: false, error: 'Method not allowed' });
  } catch (error: any) {
    console.error('[API bank/accounts] Unexpected error:', error);
    return res.status(500).json({
      success: false,
      error: error.message || 'Error inesperado en cuentas bancarias.',
    });
  }
}

export default withAuth(handler);
